import dayjs from 'dayjs';
import { useState } from 'react';
import { ExpiringGmailDelegate } from '../../../../../../types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createExpiringGmailAccountDelegate,
  deleteExpiringGmailAccountDelegate,
  UserKeys,
} from '../../../../../api/users-api';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { useStore } from '../../../../state/zustand-store';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import { handleErrors } from '../../../../../utils/errors';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import ChangeLogDialog from '../../../../../shared/change-log-dialog';
import {
  isAbleToSeeUIElement,
  UserRoles,
} from '../../../../../utils/roles/client-roles';
import { DateTimePicker } from '@mui/x-date-pickers-pro';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Check from '@mui/icons-material/Check';

interface AccountDelegationExpirationChipProps {
  userEmail: string;
  delegateEmail: string;
  currentExpirationTime: number;
  changeLogRoles: string[];
}

export default function AccountDelegationExpirationChip({
  userEmail,
  delegateEmail,
  currentExpirationTime,
  changeLogRoles,
}: AccountDelegationExpirationChipProps) {
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [expirationTime, setExpirationTime] = useState(0);

  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);

  const [actionType, setActionType] = useState<'create' | 'delete' | null>(
    null
  );

  const handleMenuClose = () => {
    setAnchorEl(null);
    setIsOpen(false);
  };

  function handleSubmit(type: 'create' | 'delete') {
    setActionType(type);

    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        UserRoles.RemoveGmailAccountDelegation.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      if (type === 'create') {
        addExpiringDelegate();
      } else {
        deleteExpiringDelegate();
      }
    }
  }

  const defaultExpirationTime = dayjs().add(1, 'w').startOf('hour');

  const { mutate: addExpiringDelegate, isPending: isAddingExpiringDelegate } =
    useMutation({
      mutationFn: () =>
        createExpiringGmailAccountDelegate(
          userEmail,
          delegateEmail,
          expirationTime === 0
            ? defaultExpirationTime.toDate().getTime()
            : expirationTime,
          changeLogMessage
        ),
      onSuccess: () => {
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'Delegate expiration added successfully!',
        });

        queryClient.setQueryData(
          [UserKeys.ExpiringGmailDelegates, userEmail],
          (old: ExpiringGmailDelegate[]) => {
            return [
              ...old,
              {
                delegateEmail,
                expirationTime:
                  expirationTime === 0
                    ? defaultExpirationTime.toDate().getTime()
                    : expirationTime,
              } as ExpiringGmailDelegate,
            ];
          }
        );

        setChangeLogMessage('');
        handleMenuClose();
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage:
            'There was an issue adding this delegate expiration. Please refresh and try again.',
        });
      },
      onSettled: () => {
        createEvent(
          analytics.user_management.user_details.gmail_settings
            .create_account_delegate_expiration,
          {
            module: ModuleList.modules.userManagement.name,
            section:
              ModuleList.modules.userManagement.sections.accountDelegation,
          }
        );

        setOpenChangeLogDialog(false);
      },
    });

  const {
    mutate: deleteExpiringDelegate,
    isPending: isDeletingExpiringDelegate,
  } = useMutation({
    mutationFn: () =>
      deleteExpiringGmailAccountDelegate(
        userEmail,
        delegateEmail,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Delegate expiration deleted successfully!',
      });

      queryClient.setQueryData(
        [UserKeys.ExpiringGmailDelegates, userEmail],
        (old: ExpiringGmailDelegate[]) => {
          return old.filter(
            (delegate) => delegate.delegateEmail !== delegateEmail
          );
        }
      );

      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue deleting this delegate expiration. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.gmail_settings
          .delete_account_delegate_expiration,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.accountDelegation,
        }
      );

      setOpenChangeLogDialog(false);
    },
  });

  return (
    <>
      {currentExpirationTime !== 0 ? (
        <Chip
          label={
            isDeletingExpiringDelegate
              ? 'Deleting...'
              : `Delegation expires ${dayjs(currentExpirationTime).format(
                  'MMM D, hA'
                )}`
          }
          size="small"
          sx={{
            width: 'fit-content',
          }}
          color="primary"
          disabled={isDeletingExpiringDelegate}
          onDelete={() => {
            handleSubmit('delete');
          }}
        />
      ) : (
        <Chip
          label="Click to set expiration"
          size="small"
          sx={{
            width: 'fit-content',
          }}
          onClick={(e) => {
            setIsOpen(true);
            setAnchorEl(e.currentTarget);
          }}
        />
      )}
      <Menu
        id="date-picker-menu"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleMenuClose}
      >
        <Stack sx={{ p: 1 }} flexDirection="row" gap={1} alignItems="center">
          <DateTimePicker
            label="Expiration Date"
            views={['year', 'month', 'day', 'hours']}
            sx={{ width: 200 }}
            value={
              expirationTime === 0
                ? defaultExpirationTime
                : dayjs(expirationTime)
            }
            disablePast
            maxDate={dayjs().add(1, 'y').endOf('day')}
            onChange={(e) => {
              if (e) {
                setExpirationTime(e.toDate().getTime());
              }
            }}
          />
          {isAddingExpiringDelegate ? (
            <Box sx={{ px: 1 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <IconButton
              onClick={() => {
                handleSubmit('create');
              }}
            >
              <Check />
            </IconButton>
          )}
        </Stack>
      </Menu>
      {openChangeLogDialog ? (
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={
            actionType === 'create'
              ? addExpiringDelegate
              : deleteExpiringDelegate
          }
          mutateLoading={isAddingExpiringDelegate || isDeletingExpiringDelegate}
        />
      ) : null}
    </>
  );
}
